import { type AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAxiosAuth from "../hooks/useAxiosAuth.ts";
import { siteGeometryQueryKey, extractSiteGeometryId } from "./useSiteGeometry.ts";
import { writeSiteQueryCache, writeGeometryQueryCache } from "./refreshSiteAndGeometryCaches.ts";

interface ResetSiteGeometryPayload {
  siteId: number | string;
  siteGeometryId?: number | string;
  siteGeometryUrl?: string;
  latitude?: number;
  longitude?: number;
}

const resetSiteGeometry = async (
  axiosAuth: AxiosInstance,
  {
    siteId,
    siteGeometryId,
    latitude,
    longitude,
  }: ResetSiteGeometryPayload
) => {
  const coordinatesSaved = latitude != null && longitude != null;

  if (coordinatesSaved) {
    await axiosAuth.patch(
      `${process.env.REACT_APP_API_BASE_URL}sites/${siteId}/`,
      { latitude, longitude }
    );
  }

  if (siteGeometryId) {
    await axiosAuth.delete(
      `${process.env.REACT_APP_API_BASE_URL}site_geometry/${siteGeometryId}/`
    );
  }

  const siteResponse = await axiosAuth.get(
    `${process.env.REACT_APP_API_BASE_URL}sites/${siteId}/set_point_neighbors/`
  );
  const site = siteResponse.data;
  const geometryId = extractSiteGeometryId(site?.sitegeometry);

  if (!geometryId) {
    return { site, geometry: null, coordinatesSaved };
  }

  const geometryResponse = await axiosAuth.get(
    `${process.env.REACT_APP_API_BASE_URL}site_geometry/${geometryId}/`
  );

  return {
    site,
    geometry: geometryResponse.data,
    coordinatesSaved,
  };
};

export function useResetSiteGeometry() {
  const axiosAuth = useAxiosAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ResetSiteGeometryPayload) =>
      resetSiteGeometry(axiosAuth, payload),
    onSuccess: (data, variables) => {
      const { site, geometry, coordinatesSaved } = data;

      if (site) {
        writeSiteQueryCache(
          queryClient,
          variables.siteId,
          site,
          geometry?.synthetic ?? true
        );
      }
      if (site?.sitegeometry && geometry) {
        writeGeometryQueryCache(queryClient, geometry);
      }
      if (variables.siteGeometryId) {
        queryClient.removeQueries({
          queryKey: ["site_geometry_geojson", variables.siteGeometryId],
        });
      }
      if (variables.siteGeometryUrl) {
        queryClient.removeQueries({
          queryKey: siteGeometryQueryKey(variables.siteGeometryUrl),
        });
      }
      if (coordinatesSaved) {
        queryClient.invalidateQueries({ queryKey: ["portfolioSites"] });
        queryClient.invalidateQueries({ queryKey: ["sites"] });
      }
    },
  });
}
